import Color from 'color';
import { darkenColor, lightenColor } from './colorOperations';
import {
    BlackTintTable,
    ColorShadeTable,
    ColorTintTable,
    HighLuminanceThreshold,
    LowLuminanceThreshold,
    LumShadeTable,
    LumTintTable,
    MAX_COLOR_RGB,
    Shade,
    WhiteShadeTable,
} from './constants';

/**
 * Using a main color and a shade, calculates a hex color which
 * will make up the calendar palette.
 *
 * @param color - the base color which other shades will be based off
 * @param shade - determines how much the base color is shaded by
 * @param isDarkMode - if dark mode is enabled, determines if palette is inverted
 * @returns a hex value representation of the shaded main color value
 */
export function getShadedColor(color: Color, shade: Shade, isDarkMode: boolean): string {
    let newColor = Color(color.rgb());

    // If the requested shade is invalid or unshaded,
    // return the hex value of the base color.
    if (shade === Shade.Unshaded || !isValidShade(shade)) {
        return color.hex();
    }

    const luminance = color.l();
    const red = color.red();
    const green = color.green();
    const blue = color.blue();
    const tableIndex = shade - 1;

    // Softening gets closer to the background color's luminance,
    // while strengthening is the opposite.
    // In light mode, softening = lightening and strengthening = darkening,
    // and in dark mode, softening = darkening and strengthening = lightening.
    let soften = lightenColor;
    let strengthen = darkenColor;
    if (isDarkMode) {
        soften = darkenColor;
        strengthen = lightenColor;
    }

    // Use the shade value (converted into tableIndex) to index into
    // the relevant color shade table according to the following cases.
    if (red === MAX_COLOR_RGB && green === MAX_COLOR_RGB && blue === MAX_COLOR_RGB) {
        // Case 1: Color is white - darken base color.
        newColor = darkenColor(newColor, WhiteShadeTable[tableIndex]);
    } else if (red === 0 && green === 0 && blue === 0) {
        // Case 2: Color is black - lighten base color by shade value.
        newColor = lightenColor(newColor, BlackTintTable[tableIndex]);
    } else if (luminance / 100 > HighLuminanceThreshold) {
        // Case 3: Color exceeds luminance threshold - strengthen color
        newColor = strengthen(newColor, LumShadeTable[tableIndex]);
    } else if (luminance / 100 < LowLuminanceThreshold) {
        // Case 4: Color is under luminance threshold - soften color
        newColor = soften(newColor, LumTintTable[tableIndex]);
    } else {
        // Case 5: Color is within luminance range, soften or strengthen
        // based on shade value.
        if (tableIndex < ColorTintTable.length) {
            newColor = soften(newColor, ColorTintTable[tableIndex]);
        } else {
            newColor = strengthen(newColor, ColorShadeTable[tableIndex - ColorTintTable.length]);
        }
    }
    return newColor.hex();
}

/**
 * Returns true if the argument is a valid Shade value.
 */
export function isValidShade(shade?: Shade): boolean {
    return typeof shade === 'number' && shade >= Shade.Unshaded && shade <= Shade.Shade8;
}
